import { useContext, useEffect, useState } from "react";
import { firestoreUpdateTransactionsAgainstRules } from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import { PlaidItemsContext } from "../firebaseio/PlaidItemsContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "../rye/RyeIcon";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { NavLabel } from "./MainNavButton";
import { useMinuteTick } from "../utils/customHooks";
import { RyeTooltip } from "../rye/RyeTooltip";
import { LeftNavStyle } from "breadcommon";
import { useOpenSettings } from "../settings_screen/settingsDefinitions";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
  useRole,
  FloatingPortal,
} from "@floating-ui/react";

dayjs.extend(relativeTime);

export function SyncControl({
  onMenuOpenChange,
}: {
  onMenuOpenChange?: (open: boolean) => void;
}): JSX.Element {
  const user = useContext(UserContext);
  const userPrefs = useContext(PreferencesContext);
  const plaidItems = useContext(PlaidItemsContext);
  const openSettings = useOpenSettings();
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [menuOpen, setMenuOpenInternal] = useState(false);
  const navStyle = userPrefs.web.left_nav_style;
  useMinuteTick();

  function setMenuOpen(open: boolean) {
    setMenuOpenInternal(open);
    onMenuOpenChange?.(open);
  }

  const { refs, floatingStyles, context } = useFloating({
    open: menuOpen,
    onOpenChange: setMenuOpen,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  async function syncTransactionsAndAccounts() {
    setSyncInProgress(true);
    try {
      await Promise.allSettled([
        callFirebaseFunction("SyncTransactions", {}),
        callFirebaseFunction("SyncAccounts", {}),
      ]);
    } catch (error) {
      console.log(`Syncing encountered an error: ${error}`);
    }
    setSyncInProgress(false);
  }

  useEffect(() => {
    syncTransactionsAndAccounts();
  }, []);

  // Analyze sync status
  let hasErrors = false;
  let oldestSyncTimestamp: number | null = null;

  for (const item of Array.from(plaidItems.values())) {
    if (
      item.sync_errors?.account_sync_error ||
      item.sync_errors?.transaction_sync_error
    ) {
      hasErrors = true;
    }
    const lastSynced =
      item.last_successful_sync_timestamps?.transaction_sync_timestamp_secs;
    if (lastSynced) {
      if (oldestSyncTimestamp === null || lastSynced < oldestSyncTimestamp) {
        oldestSyncTimestamp = lastSynced;
      }
    }
  }

  const hasPlaidItems = plaidItems.size > 0;
  const isError = hasPlaidItems && hasErrors && !syncInProgress;
  const isHealthy =
    hasPlaidItems && !hasErrors && !!oldestSyncTimestamp && !syncInProgress;

  // Determine label text
  let labelText: string;
  if (syncInProgress) {
    labelText = "Syncing...";
  } else if (isError) {
    labelText = "Sync issues";
  } else {
    labelText = "Sync";
  }

  // Tooltip text
  const isCollapsed = navStyle === LeftNavStyle.ALWAYS_COLLAPSED;
  let tooltipText: string | undefined;
  if (syncInProgress || menuOpen) {
    tooltipText = undefined;
  } else if (isError) {
    tooltipText = "Sync issues (click for actions)";
  } else if (oldestSyncTimestamp) {
    const relTime = dayjs.unix(oldestSyncTimestamp).fromNow();
    tooltipText = isCollapsed
      ? `Sync (last synced ${relTime})`
      : `Last synced ${relTime}`;
  } else {
    tooltipText = isCollapsed ? "Sync" : undefined;
  }

  // Icon color is set explicitly to avoid being overridden by button hover color
  const iconColorClass = isError
    ? "text-red"
    : isHealthy
      ? "text-green"
      : "text-on-surface-500";

  // When there are errors, clicking opens the menu. Otherwise, clicking syncs directly.
  const buttonProps = isError
    ? getReferenceProps({ ref: refs.setReference })
    : {};
  const buttonRef = isError ? refs.setReference : undefined;

  const syncButton = (
    <button
      ref={buttonRef as React.Ref<HTMLButtonElement>}
      className={buildClasses(
        "flex",
        "items-center",
        "gap-3",
        "p-2",
        "rounded-md",
        "cursor-pointer",
        "select-none",
        "min-w-0",
        "w-full",
        isError ? "text-red" : "text-on-surface-500",
        { if: !isError, then: "hover:text-on-surface-800" },
        "disabled:hover:text-on-surface-500",
      )}
      onClick={
        isError
          ? undefined
          : () => {
              firestoreUpdateTransactionsAgainstRules(user.uid);
              syncTransactionsAndAccounts();
            }
      }
      disabled={syncInProgress}
      {...buttonProps}
    >
      <RyeIcon
        name="sync"
        size="custom"
        className={buildClasses(
          { if: syncInProgress, then: "animate-spin" },
          "text-[1.2rem]",
          "block",
          "flex-shrink-0",
          iconColorClass,
        )}
      />
      <NavLabel navStyle={navStyle} text={labelText} />
    </button>
  );

  return (
    <>
      {tooltipText ? (
        <RyeTooltip text={tooltipText} placement="right">
          {syncButton}
        </RyeTooltip>
      ) : (
        syncButton
      )}

      {menuOpen && isError && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={buildClasses(
              "z-50",
              "bg-surface-50",
              "border",
              "border-on-surface-200",
              "rounded-md",
              "shadow-md",
              "overflow-hidden",
              "py-1",
            )}
          >
            <button
              className={buildClasses(
                "flex",
                "items-center",
                "gap-2",
                "w-full",
                "px-3",
                "py-2",
                "text-sm",
                "text-on-surface-700",
                "hover:bg-on-surface-100",
                "cursor-pointer",
                "whitespace-nowrap",
              )}
              onClick={() => {
                setMenuOpen(false);
                firestoreUpdateTransactionsAgainstRules(user.uid);
                syncTransactionsAndAccounts();
              }}
            >
              <RyeIcon name="sync" size="custom" className="text-[1.1rem]" />
              Try again
            </button>
            <button
              className={buildClasses(
                "flex",
                "items-center",
                "gap-2",
                "w-full",
                "px-3",
                "py-2",
                "text-sm",
                "text-on-surface-700",
                "hover:bg-on-surface-100",
                "cursor-pointer",
                "whitespace-nowrap",
              )}
              onClick={() => {
                setMenuOpen(false);
                openSettings("accounts");
              }}
            >
              <RyeIcon
                name="error_outline"
                size="custom"
                className="text-[1.1rem]"
              />
              View issues
            </button>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
